import { inject, observer } from 'mobx-react';
import React, { ReactElement } from 'react';
import { Role } from '../../models/Permissions';
import { allowsForTenant } from '../../permissions';
import Store from '../../store';
import type { RbacClaim } from '../../models/Rbac';

type CheckPermissionsProps = {
  store?: Store;
};

type CheckPermissionsState = {
  shouldShow: boolean;
};

/**
 * Whether the current user holds one of `allowedRoles` on the current tenant,
 * or is a global superKudu. The one spelling of the rule, shared by the wrapper
 * below and by any caller that has to gate without it. An RBAC user is decided
 * by their claim instead of their legacy role (see `allowsForTenant`).
 */
export function hasRole(
  store: Pick<Store, 'isSuperKudu' | 'tenant' | 'userPermissions'> & {
    rbac?: RbacClaim | null;
  },
  allowedRoles: Role[]
): boolean {
  if (store.isSuperKudu) return true;
  if (store.rbac) {
    return allowsForTenant(
      { permissions: store.userPermissions, rbac: store.rbac },
      store.tenant,
      allowedRoles
    );
  }
  return !!store.userPermissions.find(
    (permission) =>
      permission.tenant === store.tenant &&
      allowedRoles.includes(permission.role)
  );
}

export default function checkPermissions(
  allowedRoles: Role[],
  elementToRender: ReactElement
) {
  const CheckPermissions = inject('store')(
    observer(
      class CheckPermissions extends React.Component<
        CheckPermissionsProps,
        CheckPermissionsState
      > {
        constructor(props: CheckPermissionsProps) {
          super(props);
          this.state = {
            shouldShow: false,
          };
        }

        componentDidMount() {
          this.setState({
            shouldShow: hasRole(this.props.store, allowedRoles),
          });
        }

        render() {
          if (!this.state.shouldShow) {
            return null;
          }

          return elementToRender;
        }
      }
    )
  );

  return <CheckPermissions />;
}
